import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { accountsApi } from '@/lib/api/accounts';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ACCOUNT_DISPLAY_NAME_MAX_LENGTH } from '@paladin-monorepo/common';

interface AccountDisplayNameSettingsProps {
  accountId: string;
}

export function AccountDisplayNameSettings({ accountId }: AccountDisplayNameSettingsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');

  const { data: account } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountsApi.get(accountId),
    enabled: !!accountId,
  });

  const serverDisplayName = account?.displayName ?? '';
  const companyName = account?.party?.name ?? '';

  useEffect(() => {
    setDisplayName(serverDisplayName);
  }, [serverDisplayName]);

  const updateMutation = useMutation({
    mutationFn: (value: string) =>
      accountsApi.update(accountId, {
        displayName: value.trim() === '' ? null : value.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['/me'] });
      toast.success(t('account.settings.displayName.saved'));
    },
    onError: () => {
      toast.error(t('account.settings.displayName.saveFailed'));
    },
  });

  const normalizedInput = displayName.trim();
  const normalizedServer = serverDisplayName.trim();
  const isDirty = normalizedInput !== normalizedServer;
  const isTooLong = normalizedInput.length > ACCOUNT_DISPLAY_NAME_MAX_LENGTH;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('account.settings.displayName.title')}</CardTitle>
        <CardDescription>{t('account.settings.displayName.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="account-display-name">{t('account.settings.displayName.label')}</Label>
          <Input
            id="account-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={ACCOUNT_DISPLAY_NAME_MAX_LENGTH}
            placeholder={companyName || undefined}
          />
          {companyName ? (
            <p className="text-sm text-muted-foreground">
              {t('account.settings.displayName.fallbackHint', { companyName })}
            </p>
          ) : null}
          {isTooLong ? (
            <p className="text-sm text-destructive">
              {t('account.settings.displayName.tooLong', { max: ACCOUNT_DISPLAY_NAME_MAX_LENGTH })}
            </p>
          ) : null}
        </div>
        {isDirty && (
          <div>
            <Button
              onClick={() => updateMutation.mutate(displayName)}
              disabled={updateMutation.isPending || isTooLong}
            >
              {updateMutation.isPending ? t('generic.saving') : t('generic.save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
